import { useEffect, useState } from "react";
import { PackageSearch, Pencil, Plus, Search, Trash2 } from "lucide-react";
import axiosInstance from "../../api/axiosInstance";
import CatalogModal from "./CatalogModal";
import CatalogProductForm from "./CatalogProductForm";
import { buttonClass, errorMessage, inputClass, PAGE_SIZE } from "./catalogUtils";

const ENDPOINT = "/api/superadmin/catalog/products/";

export default function CatalogProducts() {
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [listError, setListError] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (search.trim() === query) return;
    const timer = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [search, query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setListError("");
    axiosInstance.get(ENDPOINT, { params: { q: query, is_active: status, page, page_size: PAGE_SIZE } })
      .then(({ data }) => {
        if (cancelled) return;
        if (!Array.isArray(data.results) || typeof data.count !== "number") {
          throw new Error("Invalid product list response");
        }
        setProducts(data.results);
        setPagination({ count: data.count, next: data.next, previous: data.previous });
      })
      .catch((requestError) => {
        if (!cancelled) {
          setProducts([]);
          setListError(requestError.message === "Invalid product list response"
            ? "The server returned an invalid product list. Please retry." : errorMessage(requestError));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query, status, page, revision]);

  function refresh() {
    setPage(1);
    setRevision((value) => value + 1);
  }
  function closeForm() {
    setForm(null);
    setError("");
  }
  async function editProduct(product) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const { data } = await axiosInstance.get(`${ENDPOINT}${product.id}/`);
      setForm({ product: data.data });
      setDeleting(null);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }
  async function saveProduct(body) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (form.product) await axiosInstance.patch(`${ENDPOINT}${form.product.id}/`, body);
      else await axiosInstance.post(ENDPOINT, body);
      setNotice(form.product ? "Product updated successfully." : "Product created successfully.");
      setForm(null);
      refresh();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }
  async function deleteProduct() {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await axiosInstance.delete(`${ENDPOINT}${deleting.id}/`);
      setDeleting(null);
      setNotice("Product deactivated successfully. Existing supplier links are preserved.");
      refresh();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-6 sm:px-[30px] sm:py-7">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-slate-900">Products</h1>
          <p className="mt-1 text-sm text-slate-500">Manage your master product catalog.</p>
        </div>
        <button type="button" disabled={busy} onClick={() => { setForm({ product: null }); setDeleting(null); setError(""); setNotice(""); }}
          className={`${buttonClass} flex items-center gap-2 bg-blue-500 text-white hover:bg-blue-600`}>
          <Plus className="h-4 w-4" aria-hidden="true" /> Create Product
        </button>
      </header>
      {listError && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{listError}</div>}
      {error && !form && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
      {notice && <div role="status" className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">{notice}</div>}

      {form && (
        <CatalogModal title={form.product ? "Edit Product" : "Create Product"} titleId="product-modal-title"
          closeLabel="Close product modal" busy={busy} onClose={closeForm} wide>
          <CatalogProductForm product={form.product} busy={busy} error={error} onSave={saveProduct} onClose={closeForm} />
        </CatalogModal>
      )}
      {deleting && (
        <section aria-label="Confirm product deletion" className="mb-6 rounded-xl border border-red-200 bg-white p-5">
          <h2 className="font-semibold text-slate-900">Deactivate {deleting.name}?</h2>
          <p className="my-2 text-sm text-slate-500">This is a soft delete. The product and its supplier links remain saved. Edit the product to reactivate it.</p>
          <div className="flex gap-2">
            <button disabled={busy} onClick={deleteProduct} className={`${buttonClass} bg-red-600 text-white hover:bg-red-700`}>{busy ? "Deleting..." : "Confirm Delete"}</button>
            <button disabled={busy} onClick={() => { setDeleting(null); setError(""); }} className={`${buttonClass} border border-slate-200 text-slate-600`}>Cancel</button>
          </div>
        </section>
      )}

      <section aria-label="Catalog products" className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" aria-hidden="true" />
            <input aria-label="Search products" placeholder="Search products..." value={search}
              onChange={(event) => setSearch(event.target.value)} className={`${inputClass} pl-9`} />
          </div>
          <select aria-label="Product status" value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
            <option value="">All statuses</option><option value="true">Active</option><option value="false">Inactive</option>
          </select>
        </div>
        {loading ? <p role="status" className="p-12 text-center text-sm text-slate-500">Loading products...</p> : listError ? (
          <div className="p-12 text-center"><button onClick={refresh} className={`${buttonClass} border border-slate-200 text-slate-600`}>Retry loading products</button></div>
        ) : !products.length ? (
          <div className="p-12 text-center text-slate-500"><PackageSearch className="mx-auto mb-3 h-8 w-8 text-violet-400" aria-hidden="true" /><p className="text-sm">No products found.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-y border-slate-100 bg-slate-50 text-xs font-medium text-slate-500">
                <tr>{["Product / SKU", "Brand", "Subcategory", "Unit", "Reference price", "Status", "Actions"].map((heading) => (
                  <th key={heading} className={`px-5 py-3 ${heading === "Actions" ? "text-right" : ""}`}>{heading}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {products.map((product) => (
                  <tr key={product.id}>
                    <td className="px-5 py-4"><p className="font-medium text-slate-900">{product.name}</p><p className="mt-1 text-xs text-slate-500">{product.sku}</p></td>
                    <td className="px-5 py-4 text-slate-500">{product.brand_name || "-"}</td>
                    <td className="px-5 py-4 text-slate-500">{product.category_name} / {product.subcategory_name}</td>
                    <td className="px-5 py-4 text-slate-500">{product.unit_type_name} ({product.unit_type_code})</td>
                    <td className="px-5 py-4 text-slate-500">{product.price ?? "-"}</td>
                    <td className="px-5 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${product.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{product.is_active ? "Active" : "Inactive"}</span></td>
                    <td className="px-5 py-4"><div className="flex justify-end gap-2">
                      <button aria-label={`Edit ${product.name}`} disabled={busy} onClick={() => editProduct(product)}
                        className="rounded-lg border border-blue-200 p-2 text-blue-500 hover:bg-blue-50 disabled:opacity-50"><Pencil className="h-4 w-4" /></button>
                      <button aria-label={`Delete ${product.name}`} disabled={busy || !product.is_active}
                        onClick={() => { setDeleting(product); setForm(null); setError(""); setNotice(""); }}
                        className="rounded-lg border border-red-200 p-2 text-red-500 hover:bg-red-50 disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && !listError && pagination.count > 0 && (
          <nav aria-label="Product pagination" className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 p-4 text-sm text-slate-500">
            <p>Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min((page - 1) * PAGE_SIZE + products.length, pagination.count)} of {pagination.count} products</p>
            <div className="flex items-center gap-3">
              <button disabled={!pagination.previous} onClick={() => setPage((value) => value - 1)} className={`${buttonClass} border border-slate-200`}>Previous</button>
              <span aria-live="polite">Page {page} of {Math.ceil(pagination.count / PAGE_SIZE)}</span>
              <button disabled={!pagination.next} onClick={() => setPage((value) => value + 1)} className={`${buttonClass} border border-slate-200`}>Next</button>
            </div>
          </nav>
        )}
      </section>
    </div>
  );
}
